import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Alert, Spinner, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ordersAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const PendingOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Ref to prevent duplicate API calls
  const hasLoadedRef = useRef(false);

  const { user } = useAuth();

  useEffect(() => {
    if (!hasLoadedRef.current && user) {
      hasLoadedRef.current = true;
      loadPendingOrders();
    }
  }, [user]);

  const loadPendingOrders = async (page = currentPage) => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page,
        limit: itemsPerPage,
        lightweight: 'super',
        fulfillmentStatus: 'picking'
      };

      const res = await ordersAPI.getAll(params);
      setOrders(res.data.data);
      setTotalCount(res.data.totalCount || res.data.count);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };



  const handleDelete = async (order) => {
    if (!window.confirm(`ลบคำสั่งซื้อ #${order.id}? สต็อกที่หยิบไปแล้วจะถูกคืน`)) return;
    try {
      await ordersAPI.delete(order.id);
      setOrders(prev => prev.filter(o => o.id !== order.id));
      setTotalCount(prev => prev - 1);
    } catch (err) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบคำสั่งซื้อ');
    }
  };

  const handleApprove = async (order) => {
    if (!window.confirm(`อนุมัติคำสั่งซื้อ #${order.id}?`)) return;
    try {
      await ordersAPI.updateStatus(order.id, { approvalStatus: 'approved' });
      setOrders(prev => prev.map(o =>
        o.id === order.id ? { ...o, approvalStatus: 'approved' } : o
      ));
    } catch (err) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการอนุมัติ');
    }
  };

  const getFulfillmentBadge = (status) => {
      switch (status) {
        case 'pending':
          return <Badge bg="secondary">รอดำเนินการ</Badge>;
        case 'reserved':
          return <Badge bg="info">จองสต็อกแล้ว</Badge>;
        case 'picking':
          return <Badge bg="primary">กำลังดำเนินการ</Badge>;
        case 'fulfilled':
          return <Badge bg="success">เสร็จสมบูรณ์</Badge>;
        default:
          return <Badge bg="secondary">{status}</Badge>;
      }
    };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB'
    }).format(amount);
  };

  // Pagination logic
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    loadPendingOrders(pageNumber);
  };

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className="bi bi-box-seam me-2"></i>
            คำสั่งซื้อที่กำลังหยิบสินค้า
          </h2>
          <p className="text-muted">คำสั่งซื้อที่อยู่ระหว่างการหยิบสินค้าในคลัง</p>
        </Col>
        {/* Only branch users can create orders */}
        {!user?.isSuperuser && !user?.isStaff && (
          <Col xs="auto">
            <Link to="/orders/create">
              <Button variant="primary">
                <i className="bi bi-plus-circle me-2"></i>
                สร้างคำสั่งซื้อใหม่
              </Button>
            </Link>
          </Col>
        )}
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Header className="bg-warning bg-opacity-10">
          <Row>
            <Col>
              <h5 className="mb-0">
                <i className="bi bi-box-seam me-2"></i>
                {`คำสั่งซื้อที่กำลังหยิบสินค้า (${totalCount} รายการ)`}
              </h5>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">กำลังโหลดข้อมูล...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-check-circle" style={{ fontSize: '3rem', color: '#28a745' }}></i>
              <p className="text-muted mt-3">ไม่มีคำสั่งซื้อที่กำลังหยิบสินค้า</p>
            </div>
          ) : (
            <>
              <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>รหัสคำสั่งซื้อ</th>
                    <th>ผู้สั่ง</th>
                    <th>วันที่สร้าง</th>
                    <th className="text-center">จำนวนรายการ</th>
                    <th className="text-end">มูลค่า</th>
                    <th>สถานะการหยิบ</th>
                    <th className="text-center">การจัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => (
                    <tr key={order.id}>
                      <td>
                        <strong>#{order.id}</strong>
                      </td>
                      <td>
                        <i className="bi bi-person me-1"></i>
                        {order.orderedBy?.fullName || '-'}
                      </td>
                      <td>
                        <i className="bi bi-calendar me-1"></i>
                        {formatDate(order.createdAt)}
                      </td>
                      <td className="text-center">{order.quantity || 0} รายการ</td>
                      <td className="text-end"><strong>{formatCurrency(order.totalCost || 0)}</strong></td>
                      <td>{getFulfillmentBadge(order.fulfillmentStatus)}</td>
                      <td className="text-center">
                        <Link to={`/orders/${order.id}`}>
                          <Button variant="info" size="sm" className="me-1" title="ดูรายละเอียด">
                            <i className="bi bi-eye"></i>
                          </Button>
                        </Link>
                        {!!(user?.isSuperuser || user?.isStaff) && order.approvalStatus !== 'approved' && (
                          <Button
                            variant="success"
                            size="sm"
                            className="me-1"
                            onClick={() => handleApprove(order)}
                            title="อนุมัติ"
                          >
                            <i className="bi bi-check-circle"></i>
                          </Button>
                        )}
                        {!!(user?.isSuperuser || user?.isStaff) && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(order)}
                            title="ลบ"
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        )}
                        {!user?.isSuperuser && !user?.isStaff && order.fulfillmentStatus !== 'fulfilled' && (
                          <Link to={`/orders/${order.id}/edit`}>
                            <Button variant="outline-primary" size="sm" className="ms-1" title="แก้ไข">
                              <i className="bi bi-pencil"></i>
                            </Button>
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3">
                  <div className="text-muted">
                    แสดง {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalCount)} จาก {totalCount} รายการ
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />

                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      // Show first page, last page, current page, and pages around current
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <Pagination.Item
                            key={pageNumber}
                            active={pageNumber === currentPage}
                            onClick={() => handlePageChange(pageNumber)}
                          >
                            {pageNumber}
                          </Pagination.Item>
                        );
                      } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                        return <Pagination.Ellipsis key={pageNumber} disabled />;
                      }
                      return null;
                    })}

                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

    </Container>
  );
};

export default PendingOrders;
